import { Grid } from '@wegroup/design-system';
import React, { useEffect, useLayoutEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import usePartyId from '../../common/src/hooks/query-params/usePartyId';
import {
  useCompanyScanDispatch,
  useCompanyScanSelector,
} from '../../common/src/utils/globalVariables';
import useSessionId from '../../flows/src/hooks/useSessionId';
import CompanyScanBottomBar from '../components/bottom-bar/CompanyScanBottomBar';
import NeedsEnrichmentModal from '../features/welcome/components/modals/NeedsEnrichmentModal';
import NoCbeModal from '../features/welcome/components/modals/NoCbeModal';
import CompanyScanPartyProvider from '../features/welcome/components/CompanyScanPartyProvider';
import WelcomeContent from '../features/welcome/components/WelcomeContent';
import useCompanyInit from '../features/welcome/hooks/useCompanyInit';
import useHandleIntroNavigation from '../features/welcome/hooks/useHandleIntroNavigation';
import { CompanyModals } from '../features/welcome/types';
import {
  setPartyId,
  setWelcomeData,
} from '../store/redux/slices/companyScanSlice';
import { WelcomeData } from '../types/formTypes';
import { useConversation } from '../../conversations/src/features/detail/shared/api/getConversationById';
import { useTranslation } from 'react-i18next';
import { isConversationUrl } from '../../app/src/features/authentication/constants/SocialAuth';

const Welcome: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { t } = useTranslation();
  const partyId = usePartyId();
  const sessionId = useSessionId();
  const { handleOnNext, handleOnPrevious, nextText, previousText, isLoading } =
    useHandleIntroNavigation();

  const companyScanDispatch = useCompanyScanDispatch();
  const welcomeData = useCompanyScanSelector(
    (state) => state.companyScan.welcomeData,
  );
  const companyCbe = useCompanyScanSelector(
    (state) => state.companyScan.selectedCompany?.company_registration?.nr,
  );

  const {
    currentModal,
    isOpen,
    onClose,
    checkCompanyRegNr,
    checkEnrichement,
    checkPid,
    isLoading: isLoadingCompanyInit,
  } = useCompanyInit(partyId);

  const { data: sessionData, isSuccess: isSessionDataAvailable } =
    useConversation(sessionId);

  const methods = useForm<WelcomeData>({
    defaultValues: welcomeData
      ? { ...welcomeData }
      : {
          expectanceAsBroker: sessionData?.broker_expectations,
          hearAboutUs: sessionData?.how_found_broker,
        },
  });

  useEffect(() => {
    if (partyId) companyScanDispatch(setPartyId(partyId));
  }, [partyId]);

  useLayoutEffect(() => {
    if (isSessionDataAvailable) {
      companyScanDispatch(
        setWelcomeData({
          expectanceAsBroker: sessionData.broker_expectations,
          hearAboutUs: sessionData.how_found_broker,
        }),
      );
      methods.setValue('expectanceAsBroker', sessionData.broker_expectations);
      methods.setValue('hearAboutUs', sessionData.how_found_broker);
    }
  }, [isSessionDataAvailable]);

  const handleNextCheck = methods.handleSubmit(async (values) => {
    companyScanDispatch(
      setWelcomeData({
        expectanceAsBroker: values.expectanceAsBroker,
        hearAboutUs: values.hearAboutUs,
      }),
    );
    if (sessionId || isConversationUrl) {
      handleOnNext();
    } else {
      if (await checkCompanyRegNr()) return;
      if (await checkPid(handleOnNext)) return;
      checkEnrichement(handleOnNext);
    }
  });

  return (
    <>
      {currentModal != null &&
        {
          [CompanyModals.NO_CBE]: (
            <NoCbeModal
              isOpen={isOpen}
              onClose={onClose}
              partyId={partyId}
              handleOnNext={() => handleNextCheck()}
              handleOnFinish={handleOnNext}
              allowContinueWithout
            />
          ),
          [CompanyModals.WRONG_CBE]: (
            <NoCbeModal
              isOpen={isOpen}
              onClose={onClose}
              partyId={partyId}
              handleOnNext={() => handleNextCheck()}
              handleOnFinish={handleOnNext}
              description={t('COMPANY-SCAN_CBE-LOOKUP_RESULT_WRONG-CBE_DESC')}
              defaultCbe={companyCbe}
            />
          ),
          [CompanyModals.NEEDS_ENRICHEMENT]: (
            <NeedsEnrichmentModal
              isOpen={isOpen}
              onClose={onClose}
              onFinish={handleOnNext}
            />
          ),
        }[currentModal]}

      <CompanyScanPartyProvider>
        <FormProvider {...methods}>
          <Grid
            templateRows="1fr auto"
            h="100%"
            data-test-id="COMPANY-SCAN_INTRO-PAGE_WRAPPER"
          >
            <WelcomeContent />
            <CompanyScanBottomBar
              isLoadingNext={isLoading || isLoadingCompanyInit}
              disabledNext={isLoading}
              nextText={nextText}
              previousText={previousText}
              onNextClick={handleNextCheck}
              onPreviousClick={handleOnPrevious}
            />
          </Grid>
        </FormProvider>
      </CompanyScanPartyProvider>
    </>
  );
};

export default Welcome;
